import React, { useEffect, useState } from 'react';
import { adminService, Category, SERVICE_CATEGORIES } from './adminService';
import { Button } from '@/components/ui/button';

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9а-я\s-]/gi, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const AdminCategoriesPage = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [newName, setNewName] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [newServiceCategory, setNewServiceCategory] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setCategories(await adminService.fetchCategories());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      setBusy(true);
      const created = await adminService.createCategory({
        name: newName.trim(),
        slug: (slugTouched ? newSlug : slugify(newName)).trim(),
        serviceCategory: newServiceCategory || null,
        description: newDescription.trim() || null,
      });
      setCategories(prev => [...prev, created]);
      setNewName('');
      setNewSlug('');
      setNewServiceCategory('');
      setNewDescription('');
      setSlugTouched(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось создать категорию');
    } finally {
      setBusy(false);
    }
  };

  const handleUpdate = async (
    id: number, name: string, slug: string, serviceCategory: string, description: string,
  ) => {
    try {
      setBusy(true);
      const updated = await adminService.updateCategory(id, {
        name: name.trim(),
        slug: slug.trim(),
        serviceCategory: serviceCategory || null,
        description: description.trim() || null,
      });
      setCategories(prev => prev.map(c => (c.id === id ? updated : c)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Удалить категорию? Баннеры, привязанные к ней, останутся без цели.')) return;
    try {
      setBusy(true);
      await adminService.deleteCategory(id);
      setCategories(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось удалить');
    } finally {
      setBusy(false);
    }
  };

  const move = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= categories.length) return;
    const next = [...categories];
    [next[index], next[target]] = [next[target], next[index]];
    setCategories(next);
    try {
      setBusy(true);
      await adminService.reorderCategories(next.map(c => c.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось изменить порядок');
      load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">Категории</h1>
        <p className="mt-1 text-sm text-gray-500">
          Управляемый список категорий. Баннеры карусели ссылаются на них (маршрут /category/&#123;slug&#125;).
        </p>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}

      {/* Создание */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Новая категория</h2>
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            type="text"
            placeholder="Название (напр. Игры)"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 outline-none focus:border-gray-400 focus:bg-white"
          />
          <input
            type="text"
            placeholder="slug (напр. games)"
            value={slugTouched ? newSlug : slugify(newName)}
            onChange={e => { setSlugTouched(true); setNewSlug(e.target.value); }}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 outline-none focus:border-gray-400 focus:bg-white sm:max-w-xs"
          />
          <select
            value={newServiceCategory}
            onChange={e => setNewServiceCategory(e.target.value)}
            title="Категория сервисов для фильтрации товаров"
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 outline-none focus:border-gray-400 focus:bg-white sm:max-w-xs"
          >
            <option value="">— фильтр не задан —</option>
            {SERVICE_CATEGORIES.map(sc => (
              <option key={sc.value} value={sc.value}>{sc.label}</option>
            ))}
          </select>
          <Button
            type="button"
            onClick={handleCreate}
            disabled={busy || !newName.trim()}
            className="rounded-xl bg-gray-900 px-6 text-white hover:bg-gray-800 disabled:opacity-40"
          >
            Добавить
          </Button>
        </div>

        <textarea
          placeholder="Описание — подзаголовок на странице категории (необязательно)"
          value={newDescription}
          onChange={e => setNewDescription(e.target.value)}
          rows={2}
          className="mt-3 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 outline-none focus:border-gray-400 focus:bg-white"
        />
      </div>

      {/* Список */}
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 p-5">
          <h2 className="text-lg font-semibold text-gray-900">Список ({categories.length})</h2>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-gray-400">Загрузка…</div>
        ) : categories.length === 0 ? (
          <div className="p-6 text-sm text-gray-400">Категорий пока нет.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {categories.map((c, i) => (
              <CategoryRow
                key={c.id}
                category={c}
                first={i === 0}
                last={i === categories.length - 1}
                busy={busy}
                onSave={handleUpdate}
                onDelete={handleDelete}
                onUp={() => move(i, -1)}
                onDown={() => move(i, 1)}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const CategoryRow: React.FC<{
  category: Category;
  first: boolean;
  last: boolean;
  busy: boolean;
  onSave: (id: number, name: string, slug: string, serviceCategory: string, description: string) => void;
  onDelete: (id: number) => void;
  onUp: () => void;
  onDown: () => void;
}> = ({ category, first, last, busy, onSave, onDelete, onUp, onDown }) => {
  const [name, setName] = useState(category.name);
  const [slug, setSlug] = useState(category.slug);
  const [serviceCategory, setServiceCategory] = useState(category.serviceCategory ?? '');
  const [description, setDescription] = useState(category.description ?? '');

  useEffect(() => {
    setName(category.name);
    setSlug(category.slug);
    setServiceCategory(category.serviceCategory ?? '');
    setDescription(category.description ?? '');
  }, [category.name, category.slug, category.serviceCategory, category.description]);

  const dirty =
    name !== category.name ||
    slug !== category.slug ||
    serviceCategory !== (category.serviceCategory ?? '') ||
    description !== (category.description ?? '');

  return (
    <li className="flex flex-col gap-3 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex items-center gap-1">
        <Button type="button" variant="outline" onClick={onUp} disabled={busy || first}
          className="h-8 w-8 rounded-lg border border-gray-200 p-0 text-gray-600 disabled:opacity-30">↑</Button>
        <Button type="button" variant="outline" onClick={onDown} disabled={busy || last}
          className="h-8 w-8 rounded-lg border border-gray-200 p-0 text-gray-600 disabled:opacity-30">↓</Button>
      </div>
      <input
        value={name}
        onChange={e => setName(e.target.value)}
        className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 outline-none focus:border-gray-400 focus:bg-white"
      />
      <input
        value={slug}
        onChange={e => setSlug(e.target.value)}
        className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500 outline-none focus:border-gray-400 focus:bg-white sm:max-w-xs"
      />
      <select
        value={serviceCategory}
        onChange={e => setServiceCategory(e.target.value)}
        title="Категория сервисов для фильтрации товаров"
        className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 outline-none focus:border-gray-400 focus:bg-white sm:max-w-[12rem]"
      >
        <option value="">— фильтр не задан —</option>
        {SERVICE_CATEGORIES.map(sc => (
          <option key={sc.value} value={sc.value}>{sc.label}</option>
        ))}
      </select>
      <div className="flex items-center gap-2">
        <Button type="button" onClick={() => onSave(category.id, name, slug, serviceCategory, description)} disabled={busy || !dirty || !name.trim()}
          className="rounded-xl bg-gray-900 px-4 text-white hover:bg-gray-800 disabled:opacity-30">Сохранить</Button>
        <Button type="button" variant="outline" onClick={() => onDelete(category.id)} disabled={busy}
          className="rounded-xl border border-red-200 px-4 text-red-600 hover:bg-red-50">Удалить</Button>
        </div>
      </div>

      <textarea
        value={description}
        onChange={e => setDescription(e.target.value)}
        rows={2}
        placeholder="Описание — подзаголовок на странице категории"
        className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500 outline-none focus:border-gray-400 focus:bg-white"
      />
    </li>
  );
};

export default AdminCategoriesPage;
